/**
 * LinkedIn OAuth callback — LinkedIn redirects here with `?code=…&state=…`.
 * We POST them to the backend `/auth/linkedin/finish` which verifies the state,
 * exchanges the code for an access token, upserts the user, and returns our JWT.
 */
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export const LinkedInCallback: React.FC = () => {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const auth = useAuth();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const code = sp.get("code");
    const state = sp.get("state");
    const err = sp.get("error_description") || sp.get("error");
    if (err) { setError(err); return; }
    if (!code || !state) { setError("Missing code or state"); return; }

    (async () => {
      try {
        const { data } = await api.post("/auth/linkedin/finish", {
          code, state, origin: window.location.origin,
        });
        localStorage.setItem("socibaba_token", data.token);
        if ((auth as any).loginWithToken) {
          await (auth as any).loginWithToken(data.token, data.user);
        }
        toast.success(`Welcome, ${data.user?.name || "friend"}!`);
        nav("/dashboard", { replace: true });
      } catch (e: any) {
        setError(e?.response?.data?.detail || e?.message || "LinkedIn sign-in failed");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[color:var(--sg-bg)] text-[color:var(--sg-fg)]" data-testid="linkedin-callback">
      {!error ? (
        <div className="flex flex-col items-center gap-3 text-sm">
          <Loader2 className="h-6 w-6 animate-spin text-[color:var(--sg-primary)]" />
          Signing you in with LinkedIn…
        </div>
      ) : (
        <div className="max-w-md text-center space-y-3">
          <AlertCircle className="h-6 w-6 mx-auto text-rose-500" />
          <div className="font-semibold">LinkedIn sign-in failed</div>
          <div className="text-sm text-[color:var(--sg-muted)]" data-testid="linkedin-callback-error">{error}</div>
          <button
            onClick={() => nav("/login", { replace: true })}
            className="mt-2 px-4 py-2 rounded-full bg-[color:var(--sg-fg)] text-white text-sm font-semibold"
          >
            Back to log in
          </button>
        </div>
      )}
    </div>
  );
};

export default LinkedInCallback;
